import { useLocalization } from '@lib/localization';
import { defaultCustomizationLocales, useGetConfigQuery, type CustomizationLocales } from '@store/api/rootEndpoints';

/** Server locale set, plus the language this particular reader wants to see it in. */
export type ResolvedCustomizationLocales = CustomizationLocales & {
  /** The reader's interface language, chosen in Settings. */
  preferred: string;
};

/**
 * The languages user-authored customization content is written in, from server config.
 *
 * Server-wide rather than per team, so the same answer holds for a type you own and for
 * one shared with you by another team — which is the whole reason it is not a team
 * setting. Falls back to the built-in default while config is still loading, so a label
 * never resolves against an empty locale set.
 *
 * `preferred` rides along so that passing this object straight to `resolveLabel` shows a
 * reader their own language, with `primary` only as the fallback. Authoring is the other
 * way round: a label is *written* in `primary`, because the API requires it.
 */
export const useCustomizationLocales = (): ResolvedCustomizationLocales => {
  const { data: config } = useGetConfigQuery();
  const { currentLanguage } = useLocalization();

  return {
    ...(config?.customizationLocales ?? defaultCustomizationLocales),
    preferred: currentLanguage,
  };
};
